import React, { useEffect, useRef, useState } from 'react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnSort,
  SortingState,
  OnChangeFn,
  PaginationState,
  getPaginationRowModel,
  ColumnResizeMode,
  ColumnSizingState,
} from '@tanstack/react-table';
import { GTRow, GTSearch, GTRowEmpty, GTHead, GTPagination } from './comps';
import { textFilter } from './util';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { getPageRange } from 'src/utils/misc';
import { captureException } from '@sentry/react';
import { GTProvider } from './GTContext';
import * as S from './GenericTable.styles';

interface GenericTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  expandContent?: (info: T) => React.ReactNode;
  itemHeight?: 50 | 77;
  searchable?: true | string[];
  emptyRow?: JSX.Element;
  initialSort?: SortingState;
  GTColumnSelectAnchorExported: JSX.Element;
  rowDisabled?: (row: T) => boolean;
  onSortChange?: (sort: SortingState) => void;
  onBottomReached?: () => void;
  layout?: {
    maxHeight?: number | string | null;
    stickyHeader?: boolean;
  };
  darkMode?: boolean;
  controledSorting?: { state: ColumnSort; onChange: (a: ColumnSort) => void };
  usePagination?: { pageSize?: number; onPaginationChange?: (e: PaginationState) => void } | null;
  tableId: TableIds;
  enableColumnResize?: boolean;
  onColumnSizingChange?: (columnSizing: ColumnSizingState) => void;
  defaultColumnSizing?: ColumnSizingState;
  hideExpandContentWhenClosed?: boolean;
  getRowCanExpand?: (row: T) => boolean;
  /** Stable row identity (default is row index) — required for expansion state to
   * survive client-side sorting/pagination instead of sticking to positions. */
  getRowId?: (row: T, index: number) => string;
  externalSearchValue?: string;
  onSearchChange?: (value: string) => void;
  searchInputRef?: React.RefObject<HTMLInputElement>;
}

const GenericTable = <T,>({
  data,
  columns,
  expandContent,
  itemHeight = 77,
  searchable,
  emptyRow,
  initialSort,
  GTColumnSelectAnchorExported,
  rowDisabled,
  onSortChange,
  onBottomReached,
  layout,
  darkMode,
  controledSorting,
  usePagination = { pageSize: 50 },
  tableId,
  enableColumnResize = true,
  onColumnSizingChange,
  defaultColumnSizing,
  hideExpandContentWhenClosed = false,
  getRowCanExpand,
  getRowId,
  externalSearchValue,
  onSearchChange,
  searchInputRef,
}: GenericTableProps<T>) => {
  const [internalGlobalFilter, setInternalGlobalFilter] = useState('');
  const globalFilter = externalSearchValue !== undefined ? externalSearchValue : internalGlobalFilter;

  const handleGlobalFilterChange = (value: string) => {
    if (externalSearchValue !== undefined && onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalGlobalFilter(value);
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const [sorting, setSorting] = useLocalStorage<SortingState>(`${tableId}-sort`, initialSort || []);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: usePagination?.pageSize || 50,
  });

  const [columnSizing, setColumnSizing] = useLocalStorage<ColumnSizingState>(
    `${tableId}-column-sizing`,
    defaultColumnSizing || {},
  );
  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');
  const isMd = useBreakpoints('md');
  const isSm = useBreakpoints('sm');
  const adjustedItemHeight = isSm ? 50 : itemHeight;

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement | null) => {
    if (containerRefElement) {
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (data.length && scrollHeight - scrollTop - clientHeight < 300) {
        onBottomReached && onBottomReached();
      }
    }
  };

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, []);

  const scrollToTable = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.style.scrollMarginTop = '160px';
      tableContainerRef.current.scrollIntoView();
      setTimeout(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.style.scrollMarginTop = '';
        }
      }, 1000);
    }
  };

  const ignoredColumns: string[] = [];

  const table = useReactTable({
    data,
    columns: ignoredColumns.length ? columns.filter((col) => !ignoredColumns.includes(col.id!)) : columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    getRowCanExpand: getRowCanExpand
      ? (row) => getRowCanExpand(row.original)
      : (row) => !!expandContent && !!expandContent(row.original),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: !!usePagination ? getPaginationRowModel() : undefined,
    onPaginationChange: setPagination,
    enableGlobalFilter: !!searchable,
    filterFns: {
      textSearch: (row, columnId, value, addMeta) => textFilter(row, columnId, value, addMeta, searchable),
    },
    onGlobalFilterChange: handleGlobalFilterChange,
    globalFilterFn: 'textSearch',
    autoResetPageIndex: false,
    state: {
      globalFilter,
      sorting,
      pagination,
      columnSizing,
    },
    initialState: {
      sorting: initialSort,
      columnSizing: columnSizing,
    },
    onSortingChange: (a) => {},
    enableColumnResizing: enableColumnResize,
    columnResizeMode,
    onColumnSizingChange: (updater) => {
      const newColumnSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newColumnSizing);
      onColumnSizingChange?.(newColumnSizing);
    },
    defaultColumn: enableColumnResize
      ? {
          minSize: 60,
          size: 160,
          maxSize: 5000,
        }
      : undefined,
  });

  useEffect(() => {
    const handleColumnSizingReset = (event: CustomEvent<{ tableId: string }>) => {
      if (event.detail.tableId === tableId) {
        setColumnSizing({});
        table.setColumnSizing({});
      }
    };

    window.addEventListener('column-sizing-reset', handleColumnSizingReset as EventListener);

    return () => {
      window.removeEventListener('column-sizing-reset', handleColumnSizingReset as EventListener);
    };
  }, [tableId, setColumnSizing, table]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    // useLocalStorage's setter is typed to take a plain value, so resolve functional
    // updaters against the current sorting state first (matching what the setter
    // would do internally anyway).
    setSorting(typeof updater === 'function' ? updater(sorting) : updater);
  };

  useEffect(() => {
    if (!!onSortChange) {
      onSortChange(sorting);
    }
  }, [sorting, onSortChange]);

  useEffect(() => {
    if (!!usePagination?.onPaginationChange) {
      usePagination.onPaginationChange(pagination);
    }
  }, [usePagination]);

  table.setOptions((prev) => ({
    ...prev,
    onSortingChange: (a) => {
      try {
        // React-table delivers sorting as a functional updater here; it is invoked bare
        // purely to read the new sort for telemetry (non-callable updaters throw into
        // the catch below).
        mpEvent(MPEvents.TableSortChange, (a as (old?: SortingState) => SortingState)()[0]);
      } catch (error) {
        captureException(error);
      }

      handleSortingChange(a);
    },
  }));

  const { start, end } = getPageRange(
    table.getState().pagination.pageIndex,
    table.getPageCount(),
    isSm ? 3 : isMd ? 5 : 7,
  );

  return (
    <GTProvider value={{ itemHeight: adjustedItemHeight }}>
      {!!searchable && (
        <GTSearch
          value={globalFilter}
          onChange={handleGlobalFilterChange}
          tableId={tableId}
          inputRef={searchInputRef}
        />
      )}
      {GTColumnSelectAnchorExported && (
        <S.AnchorWrap $itemHeight={adjustedItemHeight}>
          <div id="column-select-anchor" data-cy={`gt-sca-${tableId}`}>
            {React.cloneElement(GTColumnSelectAnchorExported, { tableId })}
          </div>
        </S.AnchorWrap>
      )}

      <S.Div
        $roundedCorners={!searchable}
        className="styled-scroll"
        data-cy={`gt-${tableId}`}
        ref={tableContainerRef}
        onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
        $maxHeight={layout?.maxHeight}
        $stickyHeader={layout?.stickyHeader}
        $darkMode={darkMode}
        $columnResizing={enableColumnResize}
        style={enableColumnResize ? { overflowX: 'auto', overflowY: 'auto' } : undefined}
      >
        <table
          style={
            enableColumnResize
              ? {
                  width: table.getCenterTotalSize(),
                  minWidth: '100%',
                }
              : undefined
          }
        >
          <GTHead
            table={table}
            controledSorting={controledSorting}
            enableColumnResize={enableColumnResize}
            itemHeight={adjustedItemHeight}
          />
          <tbody>
            {table.getRowCount() > 0
              ? table.getRowModel().rows.map((row) => {
                  let content: null | React.ReactNode = null;
                  if (expandContent) {
                    content = expandContent(row.original);
                  }
                  return (
                    <GTRow<T>
                      key={row.id}
                      row={row}
                      content={content}
                      itemHeight={adjustedItemHeight}
                      disabled={rowDisabled ? rowDisabled(row.original) : undefined}
                      tableId={tableId}
                      hideExpandContentWhenClosed={hideExpandContentWhenClosed}
                    />
                  );
                })
              : emptyRow || (
                  <GTRowEmpty text={globalFilter ? 'No results found' : undefined} itemHeight={adjustedItemHeight} />
                )}
          </tbody>
        </table>
      </S.Div>

      {usePagination && (table.getPageCount() > 1 || data.length > 20) && (
        <GTPagination
          table={table}
          isMd={isMd}
          scrollToTable={scrollToTable}
          start={start}
          end={end}
          pagination={pagination}
          setPagination={setPagination}
        />
      )}
    </GTProvider>
  );
};

export default GenericTable;
